/**
 * Factory that creates a TipTap suggestion renderer using Vue 2 + tippy.js.
 *
 * The renderer mounts a given Vue component into a tippy popup positioned at
 * the cursor. It forwards suggestion lifecycle events (onStart, onUpdate,
 * onKeyDown, onExit) to the mounted component.
 */
import Vue, { VueConstructor } from "vue";
import tippy, { Instance as TippyInstance } from "tippy.js";
import vuetify from "@/plugins/VuetifyPlugin";

export interface SuggestionItem {
  id: string;
  label: string;
  /** Only for ref mentions */
  refType?: string;
  /** Material design icon name for the suggestion list */
  icon?: string;
  /** Secondary text shown below the label */
  subtitle?: string;
  /** Whether this user is archived */
  isArchived?: boolean;
}

/** Shape of the root VM's $data used by the suggestion popup */
interface SuggestionVmData<TItem> {
  items: TItem[];
  command: (item: TItem) => void;
}

export function createSuggestionRenderer<TItem = SuggestionItem>(Component: VueConstructor) {
  return () => {
    let vm: (Vue & SuggestionVmData<TItem>) | null = null;
    let popup: TippyInstance | null = null;

    return {
      onStart(props: { items: TItem[]; command: (item: TItem) => void; clientRect: () => DOMRect }) {
        const el = document.createElement("div");

        vm = new Vue({
          vuetify,
          data(): SuggestionVmData<TItem> {
            return {
              items: props.items,
              command: props.command
            };
          },
          render(h) {
            const self = this as unknown as Vue & SuggestionVmData<TItem>;
            return h(Component, {
              props: {
                items: self.items,
                command: self.command
              }
            });
          }
        }) as Vue & SuggestionVmData;
        vm.$mount(el);

        // Wrap in a data-app div so Vuetify components render correctly
        const wrapper = document.createElement("div");
        wrapper.setAttribute("data-app", "true");
        wrapper.style.fontFamily = '"Roboto", sans-serif';
        wrapper.appendChild(vm.$el);

        popup = tippy(document.body, {
          getReferenceClientRect: props.clientRect,
          appendTo: () => document.body,
          content: wrapper,
          showOnCreate: true,
          interactive: true,
          trigger: "manual",
          placement: "bottom-start",
          maxWidth: 320
        });
      },

      onUpdate(props: { items: TItem[]; command: (item: TItem) => void; clientRect: () => DOMRect }) {
        if (!vm) return;
        vm.items = props.items;
        vm.command = props.command;
        popup?.setProps({
          getReferenceClientRect: props.clientRect
        });
      },

      onKeyDown(props: { event: KeyboardEvent }) {
        if (!vm) return false;
        const child = vm.$children[0] as (Vue & { onKeyDown?: (event: KeyboardEvent) => boolean }) | undefined;
        if (typeof child?.onKeyDown === "function") {
          return child.onKeyDown(props.event);
        }
        return false;
      },

      onExit() {
        popup?.destroy();
        vm?.$destroy();
        popup = null;
        vm = null;
      }
    };
  };
}
